import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/cn';

import { Button } from './Button';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: ReactNode;
  /** Shown above the title, e.g. a lucide icon. */
  icon?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` paints the confirm button red, for sign-outs and deletions. */
  tone?: 'default' | 'danger';
  /** Disables both buttons and spins the confirm one while the action runs. */
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The app's own confirmation dialog, in place of the browser's `window.confirm`.
 * Esc and a click on the backdrop cancel; focus starts on Cancel so a stray
 * Enter never confirms a destructive action.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  icon,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [open, loading, onCancel]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        onClick={() => !loading && onCancel()}
        aria-hidden="true"
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={message ? 'confirm-dialog-message' : undefined}
        className="bg-surface relative w-full max-w-sm rounded-2xl p-6 text-center shadow-2xl"
      >
        {icon && (
          <div
            className={cn(
              'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full',
              tone === 'danger' ? 'bg-red-50 text-red-600' : 'bg-gold-50 text-gold-700',
            )}
          >
            {icon}
          </div>
        )}
        <h2 id="confirm-dialog-title" className="text-ink text-lg font-bold">
          {title}
        </h2>
        {message && (
          <p id="confirm-dialog-message" className="text-ink-muted mt-2 text-sm leading-relaxed">
            {message}
          </p>
        )}
        <div className="mt-6 flex gap-3">
          <Button ref={cancelRef} variant="outline" fullWidth disabled={loading} onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            fullWidth
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
